import { Link } from 'react-router'
import { useMode } from '@/app/mode'
import { useAuth } from '@/auth/AuthProvider'
import { useMembers } from '@/data/client'
import { Eyebrow, Notice } from '@/ui'
import { InviteForm, MemberList, MemberRow, RoleLegend, sortMembers, useMemberActions } from './MemberAccess'
import { BackLink } from './BackLink'
import { TopBar } from './TopBar'
import type { PageProps } from './ClientRoutes'

/**
 * The client's own view of who at their company can sign in. Managers invite, change roles and remove;
 * collaborators only see the list. Staff in prep see it as a manager does, and the actions work for them too
 * (the callables allow staff with the matching permission); in preview the actions are off. The console's
 * Client access page is the fuller view, with pending invites and resends.
 */
export function PeoplePage({ clientId, client, base }: Omit<PageProps, 'config'>) {
  const { user, can } = useAuth()
  const mode = useMode()
  const members = useMembers(clientId)
  const actions = useMemberActions(clientId)
  const me = members.items.find((m) => m.id === user?.uid)
  const isStaffView = mode !== 'client'
  // Previewing is practice, and invites send real email, so the actions are off there.
  const canInvite = mode === 'preview' ? false : isStaffView ? can('clients.invite') : me?.role === 'owner'
  const canManage = mode === 'preview' ? false : isStaffView ? can('clients.members') : me?.role === 'owner'
  const manager = members.items.find((m) => m.role === 'owner')

  return (
    <>
      <TopBar clientName={client.name} section="People" homeHref={base} />
      {/* Two columns only when the invite panel is there; staff previewing get one full-width column. */}
      <main id="main" className="mx-auto grid w-full max-w-portal gap-12 px-6 py-section-sm md:grid-cols-2 md:px-10">
        {isStaffView && (
          <Notice className="md:col-span-2">
            This is what {client.name}’s managers see.{' '}
            {mode === 'preview' ? 'Inviting and changing access are off while previewing.' : 'Anything you do here is real.'} Pending invites and resends are on the console’s{' '}
            <Link to={`/staff/clients/${clientId}/people`} className="underline underline-offset-4">Client access</Link> page.
          </Notice>
        )}
        <div className="md:col-span-2">
          <BackLink fallback={base} />
        </div>
        <section className="grid content-start gap-6">
          <div className="grid gap-3">
            <Eyebrow>Your team</Eyebrow>
            <h1 className="text-h2">Who can sign in to your portal.</h1>
            <p className="text-charcoal">Everyone here is at {client.name} and works on the same profile. A role is what someone can do in this portal, not their job title.</p>
          </div>
          <RoleLegend className="border-t border-gray-200 pt-3" />
          <MemberList>
            {sortMembers(members.items).map((m) => (
              <MemberRow key={m.id} member={m} actions={actions} manage={canManage} selfId={user?.uid} />
            ))}
          </MemberList>
        </section>
        <section className="grid content-start gap-6">
          <div className="grid gap-2">
            <Eyebrow>Invite a teammate</Eyebrow>
            <p className="text-small text-dim">They get an email with a link and can pick up any section. Everything saves to one profile.</p>
          </div>
          {canInvite ? <InviteForm actions={actions} compact /> : <Notice>Only a manager can invite teammates. Ask {manager?.name || 'your portal manager'} or your Sonder contact.</Notice>}
        </section>
      </main>
    </>
  )
}
